// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Making the machine belong to the room it is standing in (M5-19).
//
// Two effects, in the order they matter:
//
// 1. **The machine's light spills onto the room.** A pinball machine is a light source — a lit
//    playfield, a backglass, flashers — and in passthrough the thing that most gives away a
//    composited object is that it does not affect anything around it. A soft pool of the
//    machine's own colour on the real floor is cheap and does most of the work.
// 2. **The machine takes the room's ambient tint.** A cabinet lit for a neon arcade, standing in
//    a warm living room at night, reads as a sticker. Sampling the room's ambient colour and
//    tinting the cabinet's *non-emissive* surfaces by a fraction of it is enough.
//
// **No raw camera access, ever.** 06 §3 makes this a non-goal, matching Chess VR. WebXR's
// `light-estimation` gives an ambient spherical harmonic and a primary light direction, which is
// derived data the runtime computes and hands over — the application never sees a pixel of the
// passthrough feed. That is a privacy property worth protecting deliberately: asking for camera
// access would put a permission prompt in front of a player who came here to play pinball.
//
// **Everything degrades to a sensible constant.** `light-estimation` is optional
// (`features.ts`), and on a runtime that does not grant it the tint is neutral and the spill is
// the machine's own colour at a fixed strength. Which is what the VR arcade does anyway.

/** What the runtime told us about the room's light. */
export interface LightEstimate {
  /** Ambient colour, linear RGB, roughly 0 … 1 each. */
  readonly r: number
  readonly g: number
  readonly b: number
  /** Overall intensity, in whatever units the runtime uses. Only its ratio matters here. */
  readonly intensity: number
}

/** What the renderer should do about it. */
export interface LightingMatch {
  /** Multiply the cabinet's non-emissive materials by this. */
  readonly tintR: number
  readonly tintG: number
  readonly tintB: number
  /** Scale the scene's own ambient by this, so a dark room does not have a daylit cabinet. */
  readonly ambientScale: number
  /** How strong the machine's spill pool on the floor should be, 0 … 1. */
  readonly spill: number
}

/** Neutral: what a runtime with no light estimation gets, and what VR always gets. */
export const NEUTRAL_MATCH: LightingMatch = {
  tintR: 1,
  tintG: 1,
  tintB: 1,
  ambientScale: 1,
  spill: 0.55,
}

/**
 * How much of the room's colour the cabinet takes.
 *
 * 0.35. Enough to sit in the room, not so much that a player with a red lamp on gets a red
 * machine — the cabinet has its own art and its own identity, and a tint that dominated it would
 * be a bug reported as "the colours are wrong on Quest".
 */
export const TINT_STRENGTH = 0.35

/**
 * The room brightness treated as "normal", in the runtime's own intensity units.
 *
 * WebXR does not define the unit, so this is a reference point rather than a measurement: an
 * estimate at this value leaves the scene's ambient alone, brighter rooms lift it and darker
 * rooms drop it. Calibrating the number properly is XR-032's on-device pass.
 */
export const REFERENCE_INTENSITY = 1.0

/** How far the ambient may be scaled either way. A pitch-dark room must not black the machine out. */
export const AMBIENT_MIN = 0.45
export const AMBIENT_MAX = 1.6

/**
 * The spill gets *stronger* in a dark room, because that is when a real machine's light pool is
 * visible at all. In a bright room it is washed out, and drawing it at full strength there is the
 * thing that reads as a fake shadow.
 */
export const SPILL_MIN = 0.2
export const SPILL_MAX = 0.85

/** Turn an estimate into instructions. `undefined` means the runtime granted no estimation. */
export function lightingMatchFor(estimate: LightEstimate | undefined): LightingMatch {
  if (estimate === undefined) return NEUTRAL_MATCH

  // Normalise the ambient colour to its own average, so what is left is *hue*, not brightness —
  // brightness is `intensity`'s job and counting it twice would double-darken a dim room.
  const average = (estimate.r + estimate.g + estimate.b) / 3
  const safe = average > 1e-4 ? average : 1
  const tint = (c: number): number => 1 + (c / safe - 1) * TINT_STRENGTH

  const intensity = Number.isFinite(estimate.intensity) && estimate.intensity > 0
    ? estimate.intensity
    : REFERENCE_INTENSITY
  const ratio = intensity / REFERENCE_INTENSITY

  return {
    tintR: tint(estimate.r),
    tintG: tint(estimate.g),
    tintB: tint(estimate.b),
    ambientScale: clamp(ratio, AMBIENT_MIN, AMBIENT_MAX),
    // Inverse: dark room, strong pool.
    spill: clamp(SPILL_MAX / (ratio > 0.2 ? ratio : 0.2), SPILL_MIN, SPILL_MAX),
  }
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo
  return v < lo ? lo : v > hi ? hi : v
}
